import { useTopicLevelTwoTimeline } from '@op/pages/Summary/hooks/useTimelineData';
import React, { useMemo, useRef, useState } from 'react';
import { Bubble, ConfigProvider, Table } from 'tea-component';
import TopicAnalysis from './TopicAnalysis';
import { LanguageType } from '@op/types/common';
import CardCommon from '@op/components/CardCommon';
import SvgIcon from '@home/components/SvgIcon';
import { getLocale, useIntl } from 'umi';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { getTeaLocale } from '@home/utils/lang';
import { NO_DATE } from '@op/constants';
import useTopicTrackingTableColumns from '@op/components/TableCommonHook/columns/topicTracking';
import './index.scss';
import { useTopicLevelOneList } from '@op/pages/Summary/hooks/useTopicConfig';
const { expandable, pageable, sortable, scrollable, autotip } = Table.addons;

export default function TopicLevelOneTable({
  filter,
  transTopic,
  language,
  goToFeeds,
  gameInfo,
  topN,
  selectTopN,
  isTopicSentiment,
  sortData,
  topicList,
}: {
  filter: any;
  transTopic: any;
  language: LanguageType;
  goToFeeds: any;
  gameInfo?: any;
  topN: number;
  selectTopN: boolean;
  isTopicSentiment: string;
  sortData: (
    list: Array<any>,
    sort: { by: string; order: 'desc' | 'asc' },
  ) => Array<any>;
  topicList: any;
}) {
  const { formatMessage } = useIntl();

  const { tableData, loading, total } = useTopicLevelOneList({
    filter,
    topicLevel: 'L1',
  });

  // 当前展开的产品
  const [expandedKeys, setExpandedKeys] = useState([]);
  const stopRef = useRef(false);

  // 批量请求图表数据
  const {
    timelineMap,
    loading: chartLoading,
    apiError: chartApiError,
  } = useTopicLevelTwoTimeline({
    filter,
    topicLevel: 'L1',
  });

  const { columns, sortList, sort, setSort } = useTopicTrackingTableColumns({
    timelineMap,
    filter,
    transTopic,
    topicLevel: 'L1',
    gameInfo,
    isTopicSentiment,
  });

  const records = useMemo(() => {
    if (selectTopN) {
      const sortInfo: { by: string; order: 'desc' | 'asc' } = {
        by: 'mentions',
        order: 'desc',
      };
      const list = tableData?.sort(sortable.comparer([sortInfo])) || [];

      const topNList = list.slice(0, topN);

      return sortData(topNList, sort);
    }

    return sortData(tableData, sort);
  }, [tableData, sort, selectTopN, topN]);

  const locale = getTeaLocale(getLocale());

  function getTableAddons() {
    const addons = [
      sortable({
        columns: sortList.map((item) => ({ prefer: 'desc', key: item })),
        value: [sort],
        onChange: (value) => {
          const sort: any = value?.[0] || {
            by: 'mentions',
            order: 'desc',
          };
          buriedBtnPoint('STO_ListRank', sort.by);
          setSort(sort);
        },
      }),
      scrollable({
        minWidth: 1500,
      }),
      autotip({
        emptyText: formatMessage({ id: NO_DATE.SELECTION }),
      }),
      expandable({
        expandedKeys,
        onExpandedKeysChange: (keys, { operateKey, operateType }) => {
          if (stopRef.current) return;

          if (operateType === 'expand') {
            buriedBtnPoint('STO_TopicOpenList', operateKey);
          }
          setExpandedKeys(keys);
        },
        rowExpand: true,
        render(record) {
          const topics = record.child_topics;

          return (
            <TopicAnalysis
              transTopic={transTopic}
              filter={filter}
              topics={topics}
              language={language}
              topicList={topicList}
              topicLevel="L1"
              sortData={sortData}
              goToFeeds={goToFeeds}
              currentTopic={record.topic}
              gameInfo={gameInfo}
            ></TopicAnalysis>
          );
        },
      }),
    ];

    if (records.length > 10 && !selectTopN) {
      addons.push(pageable());
    }
    return addons;
  }

  return (
    <CardCommon contentCard loading={loading} loadingHeight={565}>
      <ConfigProvider locale={locale}>
        <Table
          className="dbo-topic-tracking-table"
          bordered="all"
          records={records}
          recordKey={'topic'}
          columns={[
            {
              key: 'keyTopics',
              header: formatMessage({ id: 'Key Topics' }),
              render: (record) => {
                return (
                  <div className="dbo-topic-tracking-table-title">
                    <div className="dbo-topic-tracking-table-topic">
                      {transTopic(record.topic, 'L1') +
                        ` (${record.child_topics.length})`}
                    </div>
                    <Bubble
                      dark={true}
                      content={formatMessage({ id: 'Go to Feeds' })}
                    >
                      <div style={{ display: 'inline-block' }}>
                        <SvgIcon
                          type="view-details-icon"
                          onClick={() => {
                            goToFeeds(record.topic, 'L1');
                            buriedBtnPoint('STO_TopicGoFeeds', record.topic);
                            stopRef.current = true;
                          }}
                        />
                      </div>
                    </Bubble>
                  </div>
                );
              },
            },
            ...columns,
          ]}
          addons={getTableAddons()}
        />
      </ConfigProvider>
    </CardCommon>
  );
}
